/**
 * Construction of the ScoreTracker instance.
 *
 * A pure factory: it takes a validated config and the two providers, and returns an instance that
 * has not started. Constructing is side-effect free by design in the SDK, so nothing happens until
 * the caller awaits `init()`.
 */

import { ScoreTracker } from '@3halves-labs/score-tracker';

/** Batch size and cadence. The SDK's own defaults, stated here so they are visible. */
const BATCH = { max: 20, intervalMs: 2500 };

/** Scroll milestones, in percent of document height. */
const SCROLL_THRESHOLDS = [ 25, 50, 75, 100 ];

/**
 * Property names scrubbed from every event before it leaves the browser.
 *
 * Defence in depth, not the policy itself: custom events are supposed to carry no personal data at
 * all. This catches the obvious names if a future caller passes one by accident, recursively and
 * including nested objects.
 *
 * **A name here must not collide with a field of the envelope's `context`.** The SDK builds the
 * body as `{ context: scrubPii(ctx), events: [...scrubPii(props)] }` — the same rule map, applied
 * to both — and a `drop` rule deletes the key wherever it appears. `jwt` was on this list and
 * `context.jwt` is where the signed-in fan's access token rides, so every batch went out with the
 * fan scrubbed off it and identified tracking was silently anonymous tracking. Nothing failed:
 * the ingest answered 202 to an envelope that simply had no fan in it.
 *
 * `token` stays. It is not a context field, so it costs nothing and still catches a prop.
 * tracker.test.js drives the real SDK and asserts both halves of this.
 *
 * **`userId` joined the load-bearing list when `identify()` was wired.** It is a context field like
 * `jwt`, and since the fan is now stamped into `context.userId` as well, adding it here would
 * anonymise every identified batch in exactly the same silent way -- a 202 over an envelope with no
 * fan in it.
 */
export const PII_DENYLIST = [
	'email',
	'emailAddress',
	'name',
	'firstName',
	'lastName',
	'fullName',
	'phone',
	'password',
	'token',
];

/**
 * The storage keys the SDK derives a VISITOR from, as of `1.21.2`.
 *
 * Every one of them is read in the constructor and nowhere else: `score.anonId` and
 * `score.sessionId` through `getOrCreate()`, `score.sessionCount`/`score.lastSession` through
 * `bumpSessionCountIfNew()`, and `score.groups` through `loadGroups()`. That is why an account
 * switch needs a new instance rather than a restart -- see `clearVisitorIdentity()`.
 *
 * `score.sessionId` is the only one in `sessionStorage`; the rest are in `localStorage`, which is
 * why the previous fan's visitor id survives a navigation and even a browser restart.
 */
const VISITOR_KEYS = {
	local: [ 'score.anonId', 'score.sessionCount', 'score.lastSession', 'score.groups' ],
	session: [ 'score.sessionId' ],
};

/**
 * The prefix of the SDK's identity-link latch, added in `1.21.2`.
 *
 * `linkAnonymous()` writes `score.linked.{anonymousId}.{userId}` once the link POST succeeds and
 * skips the call while it is present, so the key is not a fixed name and `VISITOR_KEYS` cannot
 * reach it -- it has to be swept by prefix.
 *
 * **Clearing it is not what makes an account switch correct.** The key names both ids, so the next
 * fan's fresh `anonId` already yields a fresh key and their link fires regardless. It is cleared
 * because it carries the PREVIOUS fan's `sub` in a localStorage key name that would otherwise
 * outlive their session, which is the thing `clearVisitorIdentity()` is named after.
 */
const LINK_KEY_PREFIX = 'score.linked.';

/**
 * Forget who this browser is, so the next `new ScoreTracker` mints a fresh visitor.
 *
 * **Called only from the account-switch path**, and it is the half of that reset the SDK offers no
 * API for: `anonymousId` is a private field assigned in the constructor, there is no `reset()`, and
 * `shutdown()` touches no storage at all. Without this a restart carries the previous fan's
 * `anonymousId` into every envelope sent under the new fan's `jwt`, which is a cross-fan link in a
 * third party's warehouse.
 *
 * Every access is guarded: private-browsing modes throw on access rather than on use, and a failed
 * clear must not be the reason a page's tracking dies. The consequence of a failure is the stale
 * visitor id this exists to remove, so it is not silent -- it warns.
 *
 * @return {void}
 */
export function clearVisitorIdentity() {
	[
		[ 'localStorage', VISITOR_KEYS.local ],
		[ 'sessionStorage', VISITOR_KEYS.session ],
	].forEach( ( [ store, keys ] ) => {
		try {
			keys.forEach( key => window[ store ].removeItem( key ) );

			if ( store === 'localStorage' ) {
				linkLatchKeys( window[ store ] ).forEach( key =>
					window[ store ].removeItem( key )
				);
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.warn( `[tracking] could not clear ${ store } visitor keys`, error );
		}
	} );
}

/**
 * Every identity-link latch currently in a store.
 *
 * Collected before removing any, because removing while walking `key( i )` by index skips entries.
 *
 * @param {Storage} store The store to scan.
 * @return {string[]} The matching keys.
 */
function linkLatchKeys( store ) {
	const keys = [];

	for ( let i = 0; i < store.length; i++ ) {
		const key = store.key( i );

		if ( typeof key === 'string' && key.startsWith( LINK_KEY_PREFIX ) ) {
			keys.push( key );
		}
	}

	return keys;
}

/**
 * @param {Object}                           options            Collaborators.
 * @param {import('./config').TrackerConfig} options.config     Validated configuration.
 * @param {Function}                         options.getConsent Consent predicate.
 * @param {Function}                         options.getToken   Token provider.
 * @return {ScoreTracker} An unstarted instance.
 */
export function createTracker( { config, getConsent, getToken } ) {
	return new ScoreTracker( {
		publishableKey: config.publishableKey,
		siteId: config.siteId,
		endpoint: config.endpoint,
		batch: BATCH,
		// Honour the browser's Do Not Track header: the tracker stays idle regardless of consent.
		respectDNT: true,
		autoPageviews: true,
		// Known to be noise on this site, and switched on anyway -- the client was shown the
		// measurement and chose to keep it, so filtering happens on the 3Halves side. Several theme
		// components call history.replaceState with the URL they were already on during
		// initialisation, and the SDK emits route_change on every such call whether or not anything
		// changed: measured at one phantom event per pageview on the home page and two on an
		// article. There is no real navigation to capture either, because this is a multi-page site
		// and every navigation is a full load that already emits its own pageview.
		autoRouteChange: true,
		autoScroll: SCROLL_THRESHOLDS,
		autoVideo: true,
		piiDenylist: PII_DENYLIST,
		debug: config.debug,
		getConsent,
		getToken,
	} );
}
